import React, { useState, useEffect, useCallback } from 'react';
import TTSPlayer from './TTSPlayer';
import { motion, AnimatePresence } from 'framer-motion';
import apiService from '../services/api';

const ReadingRoom = ({ 
  session, 
  onClose, 
  onAddNote, 
  onUpdateProgress 
}) => {
  const [readingProgress, setReadingProgress] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);
  const [isReading, setIsReading] = useState(true); // Auto-start reading
  const [selectedText, setSelectedText] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Fetch PDF with authentication
  const fetchPdf = useCallback(async () => {
    if (!session?.fileUploadId) return;
    
    try {
      setPdfLoading(true);
      setPdfError(null);
      
      // Construct the correct PDF URL
      const baseURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const pdfUrl = `${baseURL}/api/v1/reading/files/${session.fileUploadId}/content`;
      
      const response = await fetch(pdfUrl, {
        headers: {
          'Authorization': `Bearer ${apiService.getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfBlobUrl(blobUrl);
      
    } catch (error) {
      console.error('Error fetching PDF:', error);
      setPdfError(error.message);
    } finally {
      setPdfLoading(false);
    }
  }, [session?.fileUploadId]);

  useEffect(() => {
    fetchPdf();

    // Cleanup blob URL on unmount
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [fetchPdf]);

  // Timer for tracking reading time
  useEffect(() => {
    let interval;
    if (isReading) {
      interval = setInterval(() => {
        setTimeSpent(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isReading]);

  // Calculate reading progress based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const iframe = document.querySelector('.pdf-iframe');
      if (iframe && iframe.contentWindow) {
        try {
          const scrollTop = iframe.contentWindow.scrollY;
          const scrollHeight = iframe.contentWindow.document.body.scrollHeight;
          const clientHeight = iframe.contentWindow.document.body.clientHeight;
          const maxScroll = scrollHeight - clientHeight;
          
          if (maxScroll > 0) {
            const progress = (scrollTop / maxScroll) * 100;
            setReadingProgress(Math.min(100, Math.max(0, progress)));
          }
        } catch (e) {
          // Cross-origin restrictions might prevent access
        }
      }
    };

    const iframe = document.querySelector('.pdf-iframe');
    if (iframe) {
      iframe.addEventListener('load', () => {
        iframe.contentWindow.addEventListener('scroll', handleScroll);
      });
    }

    return () => {
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.removeEventListener('scroll', handleScroll);
      }
    };
  }, [pdfBlobUrl]);

  const handleTextSelection = () => {
    const selection = window.getSelection();
    if (selection.toString().trim()) {
      setSelectedText(selection.toString());
    }
  };

  const handleAddNote = () => {
    if (selectedText.trim()) {
      setShowNoteInput(true);
      setNoteContent(selectedText);
    }
  };

  const handleSaveNote = () => {
    if (noteTitle.trim() && noteContent.trim()) {
      onAddNote({
        noteTitle: noteTitle,
        noteContent: noteContent,
        pageNumber: 1, // Since we removed page navigation
        highlightStart: 0,
        highlightEnd: selectedText.length,
        noteType: 'highlight'
      });
      setNoteTitle('');
      setNoteContent('');
      setSelectedText('');
      setShowNoteInput(false);
    }
  };

  const handleCancelNote = () => {
    setNoteTitle('');
    setNoteContent('');
    setSelectedText('');
    setShowNoteInput(false);
  };

  const handleProgressUpdate = () => {
    onUpdateProgress({
      currentPage: 1,
      readingProgress,
      timeSpentReading: timeSpent,
      readingSpeedWpm: 200
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClose = () => {
    if (timeSpent > 30) { // If spent more than 30 seconds, ask for confirmation
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 z-50"
    >
      {/* Modern Header */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left Section - Title and Close */}
            <div className="flex items-center space-x-4">
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all duration-200 group"
                title="Close Reading Room"
              >
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                    {session?.subject} - {session?.chapter}
                  </h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Reading in progress
                  </p>
                </div>
              </div>
            </div>

            {/* Center Section - Progress Bar */}
            <div className="flex-1 max-w-md mx-8">
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                    <span>Progress</span>
                    <span>{readingProgress.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <motion.div
                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${readingProgress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Section - Time and Audio Player */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {formatTime(timeSpent)}
                </span>
              </div>

              {/* Compact TTS Player */}
              {session?.extractedText && (
                <div className="w-64">
                  <TTSPlayer content={session.extractedText} compact={true} />
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                    TTS works best with shorter text
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Full Screen PDF */}
      <div className="flex-1 h-full overflow-hidden">
        {pdfLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
                <div className="absolute inset-0 w-16 h-16 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin mx-auto" style={{ animationDelay: '0.5s' }}></div>
              </div>
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">Loading your reading material...</h3>
              <p className="text-gray-500 dark:text-gray-400">Preparing an engaging learning experience</p>
            </div>
          </div>
        ) : pdfError ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md">
              <div className="w-20 h-20 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Failed to load PDF</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">{pdfError}</p>
              <button
                onClick={fetchPdf}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : pdfBlobUrl ? (
          <div className="h-full relative">
            <iframe
              src={pdfBlobUrl}
              className="w-full h-full border-0 pdf-iframe"
              title="PDF Viewer"
              onLoad={handleTextSelection}
            />
            
            {/* Floating Action Buttons for Selected Text */}
            {selectedText && (
              <div className="fixed bottom-6 right-6 flex space-x-2 z-10">
                <motion.button
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  onClick={handleAddNote}
                  className="bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-full shadow-lg transition-all duration-200"
                  title="Add note for selected text"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </motion.button>
                
                <motion.button
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  onClick={() => {
                    // Create a temporary TTS player for selected text
                    const tempContent = { summary: selectedText };
                    // You could implement a temporary TTS player here
                    console.log('Selected text for TTS:', selectedText);
                  }}
                  className="bg-purple-500 hover:bg-purple-600 text-white p-3 rounded-full shadow-lg transition-all duration-200"
                  title="Listen to selected text"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                </motion.button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">PDF not available</h3>
              <p className="text-gray-600 dark:text-gray-400">The reading material could not be loaded</p>
            </div>
          </div>
        )}
      </div>

      {/* Notes Modal */}
      <AnimatePresence>
        {showNoteInput && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-4"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add Note</h3>
                  <button
                    onClick={handleCancelNote}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <input
                  type="text"
                  placeholder="Note title (required)"
                  value={noteTitle}
                  onChange={(e) => setNoteTitle(e.target.value)}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg mb-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                
                <textarea
                  placeholder="Note content"
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg mb-4 h-32 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                
                <div className="flex space-x-3">
                  <button
                    onClick={handleSaveNote}
                    disabled={!noteTitle.trim()}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Save Note
                  </button>
                  <button
                    onClick={handleCancelNote}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Close Confirmation Modal */}
      <AnimatePresence>
        {showCloseConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-4"
            >
              <div className="p-6 text-center">
                <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Save your progress?</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  You've been reading for {formatTime(timeSpent)}. Would you like to save your progress before closing?
                </p>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      handleProgressUpdate();
                      onClose();
                    }}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Save & Close
                  </button>
                  <button
                    onClick={() => setShowCloseConfirm(false)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                  >
                    Continue Reading
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ReadingRoom; 